import { BREAKPOINTS, Button, Card, Form, Heading, Select } from '@boltenergy-be/design-system'
import { useTranslation } from 'next-i18next/pages'
import styles from './StartSimulationCard.module.scss'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { PostalCodeAndTown } from 'components/inputs'
import { ClientType, EnergyType, StartSimulationFormInputs, StartSimulationFormProps } from 'components/start-simulation-card/types'
import React, { useEffect, useState } from 'react'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { CustomerFlows, DeliveryAddressFieldKeys, InputGroupKeys, PersonalDataFieldKeys, SimulationFieldKeys } from 'store/customer/enums'
import { AddressFields } from 'components/inputs/address/types'
import { checkIsEavOrFixed, startSimulationOrRegistration } from 'utils/customerFlow'
import { SimulationSteps } from 'features/simulation/types'
import { goToStep, updateAllInputs, updateInput, updateSimulatedPropositions } from 'store/customer/slice'
import { getUsableProducts } from 'utils/products'
import { determineSimulationUsage, requestAndSavePropositions } from 'features/simulation/utils'
import { addLogBreadcrumb, log } from 'utils/logging'
import { PhoneAvailability } from 'components/phone-availibility/PhoneAvailability'
import classNames from 'classnames'
import { FEATURE_FLAGS } from 'constants/tracking'
import { useFeatureFlagTranslatedPayload } from 'hooks/useFeatureFlagTranslatedPayload'
import ReviewRatings from 'components/review-ratings/ReviewRatings'
import { useWindowSize } from 'usehooks-ts'
import { useFeatureFlagVariantKey } from 'posthog-js/react'
import { useGetTownsQuery } from 'store/api/bolt-api'
import { POSTAL_CODE } from 'constants/regex'
import Link from 'components/link/Link'

const StartSimulationCard = ({
  allowedClientTypes,
  specificProduct,
  fullWidth,
  onSubmitExtraActions,
  className
}: StartSimulationFormProps) => {
  // i18n
  const { t } = useTranslation(['simulation', 'common', 'lookingForHelp'])

  // Window size
  const { width } = useWindowSize({ initializeWithValue: false })
  const isTablet = !!width && width < BREAKPOINTS.tablet // Based on $widthDesktop

  // Posthog
  // Feature flags resolve client-side only (and can already be cached in localStorage before hydration),
  const [hasMounted, setHasMounted] = useState(false)
  useEffect(() => setHasMounted(true), [])
  const variant = useFeatureFlagVariantKey(FEATURE_FLAGS.googleReviews)
  const showGoogleReviews = hasMounted && variant === 'google'
  const headlineText = useFeatureFlagTranslatedPayload({
    key: FEATURE_FLAGS.simulationCardHeadline,
    fallback: t('simulation:startSimulationCard.title')
  })

  // Redux store
  const { inputs, propositions } = useStoreSelector((store) => store.customer)
  const { personalData, simulation, deliveryAddress } = inputs
  const { didSimulation, currentStep } = useStoreSelector((store) => store.customer.flows.simulation)
  const dispatch = useStoreDispatch()

  // React Hook Form
  const hookForm = useForm<StartSimulationFormInputs>({
    defaultValues: {
      clientType: personalData[PersonalDataFieldKeys.IS_COMPANY] ? ClientType.PROFESSIONAL : ClientType.RESIDENTIAL,
      energyType: deliveryAddress[DeliveryAddressFieldKeys.NEEDS_GAS] ? EnergyType.ELECTRICITY_GAS : EnergyType.ELECTRICITY,
      postalCode: deliveryAddress[DeliveryAddressFieldKeys.ADDRESS]?.[AddressFields.POSTAL_CODE] || ''
    },
    mode: 'onBlur'
  })
  const {
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    setError,
    control
  } = hookForm
  const watchEnergyType = useWatch({ control, name: 'energyType' })
  const watchPostalCode: string = useWatch({ control, name: 'postalCode' })

  // Redux query
  const { currentData: townsData, isFetching: isFetchingTowns } = useGetTownsQuery(watchPostalCode, {
    skip: !POSTAL_CODE.test(watchPostalCode ?? '')
  })

  // Local state
  const [loading, setLoading] = useState<boolean>(false)

  // Constants
  const isEavOrFixed = checkIsEavOrFixed(inputs.simulation.chosenSimulationType)

  /**
   * Triggered everytime the personalData inputs change (Needed because the page loads before the values are in the store)
   * Sets the values in the form if they change in the store
   */
  useEffect(() => {
    setValue('clientType', personalData[PersonalDataFieldKeys.IS_COMPANY] ? ClientType.PROFESSIONAL : ClientType.RESIDENTIAL)
    setValue('energyType', deliveryAddress[DeliveryAddressFieldKeys.NEEDS_GAS] ? EnergyType.ELECTRICITY_GAS : EnergyType.ELECTRICITY)
    setValue('postalCode', deliveryAddress[DeliveryAddressFieldKeys.ADDRESS]?.[AddressFields.POSTAL_CODE] || '')
  }, [personalData, deliveryAddress, setValue])

  /**
   * Handles the form submit after validation by React Hook Form
   *
   * @param {StartSimulationFormInputs} data
   */
  const onSubmit = async (data: StartSimulationFormInputs) => {
    addLogBreadcrumb('StartSimulationCard: form submitted', {
      postalCode: data.postalCode,
      clientType: data.clientType,
      energyType: data.energyType
    })
    setLoading(true)

    try {
      // Redirect to the postal code step if no postal code was filled in
      if (!data.postalCode) {
        dispatch(goToStep({ flow: CustomerFlows.SIMULATION, step: SimulationSteps.POSTAL_CODE }))
      }

      if (data.postalCode && (!data.region || !data.townName)) {
        if (!townsData?.towns?.length) {
          setError('postalCode', { type: 'validate', message: t('common:error') }, { shouldFocus: true })
          return
        }

        const { region, towns } = townsData
        data.region = region

        if (!data.townName) data.townName = towns?.[0]?.townName
        if (!data.townCode) data.townCode = towns?.[0]?.townCode
      }

      // Define booleans
      const isCompany = data.clientType === ClientType.PROFESSIONAL
      const needsGas = data.energyType === EnergyType.ELECTRICITY_GAS

      // Check if the input has changed
      const hasChangedInput =
        deliveryAddress[DeliveryAddressFieldKeys.ADDRESS][AddressFields.POSTAL_CODE] !== data.postalCode ||
        personalData[PersonalDataFieldKeys.IS_COMPANY] !== isCompany ||
        deliveryAddress[DeliveryAddressFieldKeys.NEEDS_GAS] !== needsGas

      // Update the store
      dispatch(
        updateAllInputs({
          inputs: {
            ...inputs,
            [InputGroupKeys.DELIVERY_ADDRESS]: {
              [DeliveryAddressFieldKeys.NEEDS_GAS]: needsGas,
              [DeliveryAddressFieldKeys.ADDRESS]: {
                ...personalData[DeliveryAddressFieldKeys.ADDRESS],
                postalCode: data.postalCode,
                townCode: data.townCode,
                townName: data.townName
              }
            },
            [InputGroupKeys.PERSONAL_DATA]: {
              ...personalData,
              [PersonalDataFieldKeys.IS_COMPANY]: isCompany
            },
            [InputGroupKeys.SIMULATION]: {
              ...simulation,
              [SimulationFieldKeys.REGION]: data.region
            }
          }
        })
      )

      // Extra actions after the form submission
      await onSubmitExtraActions?.()

      // If they changed the input & already did a simulation OR there was a specific product selected,
      // we need to reset the simulated propositions & simulate again for the new usable products
      if ((didSimulation && hasChangedInput) || propositions.simulateSpecificProduct) {
        // Reset the simulated propositions
        dispatch(updateSimulatedPropositions({ propositions: null }))
        // Get the new usable products
        const usableProducts = getUsableProducts({ region: data.region })

        // If they changed the input of the form, we need to update the usage
        if (hasChangedInput) {
          dispatch(updateInput({ group: InputGroupKeys.SIMULATION, key: SimulationFieldKeys.USAGE, value: determineSimulationUsage() }))
        }

        await requestAndSavePropositions({ products: usableProducts })
        addLogBreadcrumb('StartSimulationCard: re-simulation triggered', { products: usableProducts })
      }

      const startFromUsage = data.postalCode && [SimulationSteps.POSTAL_CODE, SimulationSteps.ENERGY_TYPE].includes(currentStep)

      addLogBreadcrumb('StartSimulationCard: navigating to simulation/registration', { specificProduct, startFromUsage })
      return startSimulationOrRegistration({
        specificProduct,
        startFromUsage
      })
    } catch (error) {
      log({ error: 'Failed to submit StartSimulationForm', identifier: '[simulation:StartSimulationForm]' })
      return
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={classNames(styles['start-simulation-card-wrapper'], className)}>
      <Card as="section" color="soft" className={classNames(styles['start-simulation-card'], { [styles.fullwidth]: fullWidth })}>
        <Heading suppressHydrationWarning as="h2" variant="h4" serif>
          {headlineText}
        </Heading>

        <FormProvider {...hookForm}>
          <Form onSubmit={handleSubmit(onSubmit)} className={styles['start-simulation-card-form']}>
            <PostalCodeAndTown hideErrorMessage hasRegionField hiddenLabel hideTownSelectField required={false} />

            <Select
              {...register('clientType', {
                required: t('validation:required')
              })}
              disabled={allowedClientTypes?.length === 1}
              error={!!errors?.clientType?.message}
              hiddenLabel
              label={t('simulation:fields.clientType.label')}
            >
              <option key={ClientType.RESIDENTIAL} value={ClientType.RESIDENTIAL}>
                {t('common:residential')}
              </option>
              <option key={ClientType.PROFESSIONAL} value={ClientType.PROFESSIONAL}>
                {t('common:company')}
              </option>
            </Select>

            <Select
              {...register('energyType', {
                required: t('validation:required')
              })}
              error={errors?.energyType?.message}
              hiddenLabel
              label={t('simulation:fields.energyType.label')}
              prefix={{
                as: watchEnergyType === EnergyType.ELECTRICITY ? 'electricity' : 'electricityGas'
              }}
            >
              <option key={EnergyType.ELECTRICITY} value={EnergyType.ELECTRICITY}>
                {t('common:electricity')}
              </option>
              <option key={EnergyType.ELECTRICITY_GAS} value={EnergyType.ELECTRICITY_GAS}>
                {t('common:electricityAndGas')}
              </option>
            </Select>

            <Button
              suppressHydrationWarning
              className={styles.cta}
              isFullwidthMobile
              loading={loading || isFetchingTowns}
              size="large"
              type="submit"
            >
              {didSimulation || isEavOrFixed ? t('common:switchNow') : t('common:calculateProposal')}
            </Button>
          </Form>
        </FormProvider>

        <footer className={styles.footer}>
          <small>
            <strong>
              {t('lookingForHelp:needHelp')}{' '}
              <Link as="a" href="tel:028993300" variant="inline">
                {t('lookingForHelp:callNumber')}
              </Link>
            </strong>
            . <PhoneAvailability />
          </small>
        </footer>
      </Card>

      {isTablet && !showGoogleReviews && <ReviewRatings className={styles.reviews} show={{ googleReviews: true, vnr: true }} />}
    </div>
  )
}

export default StartSimulationCard
